/**
 * frontend/src/pages/valuation/_OverviewDocuments.tsx
 *
 * VAL-OVERVIEW-1C R7 — issued documents. This is the issued-document
 * archive: valuation reports the platform produced, keyed by
 * snapshot_version, superseded versions included on purpose. These are NOT
 * uploaded source documents — valuation has no upload route at all, so this
 * block never offers one.
 *
 * VAL-HISTORY-4a — the list + download handler live in the exported
 * `IssuedDocumentList` so `_SnapshotHistory.tsx` can render the same rows,
 * filtered per version, without a second download function. Overview keeps
 * its own loading/error/empty states around it; those differ by caller
 * (Overview's empty text is not History's), so they stay here rather than
 * moving into the shared component.
 */
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchValuationDocuments, type ValuationDocumentRow } from '@/api/valuation'
import { downloadArchivedDocument } from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import { formatDateTime } from '@/utils/date'

function extensionOf(doc: ValuationDocumentRow): string {
  const match = /\.([a-zA-Z0-9]+)$/.exec(doc.filename)
  return match ? match[1] : doc.media_type
}

export const isPdf = (d: ValuationDocumentRow) => d.media_type === 'application/pdf'

/** The one download handler — per-row buttons and the Next step bar both call it. */
export function useDocumentDownload(engagementId: string) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  async function handleDownload(doc: ValuationDocumentRow) {
    setDownloadingId(doc.document_id)
    try {
      const { blob, filename } = await downloadArchivedDocument(engagementId, doc.document_id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      toast.success(`${filename} downloaded.`)
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setDownloadingId(null)
    }
  }

  return { downloadingId, handleDownload }
}

/** Display only — no row is hidden or changed. Newest of each file type for the
 *  live version is `Latest`; other live-version rows are `Earlier copy`. */
function badgeFor(
  doc: ValuationDocumentRow,
  liveVersion: number | null | undefined,
  newestId: Map<boolean, string>,
): string | null {
  if (liveVersion === undefined || liveVersion === null) return null
  if (doc.snapshot_version !== liveVersion) return 'Earlier record'
  return newestId.get(isPdf(doc)) === doc.document_id ? 'Latest' : 'Earlier copy'
}

export function IssuedDocumentList({
  engagementId,
  documents,
  liveVersion,
}: {
  engagementId: string
  documents: ValuationDocumentRow[]
  /** Live snapshot version; when given, rows carry Latest / Earlier badges. */
  liveVersion?: number | null
}) {
  const { downloadingId, handleDownload } = useDocumentDownload(engagementId)

  const sorted = [...documents].sort(
    (a, b) => new Date(b.produced_at).getTime() - new Date(a.produced_at).getTime(),
  )
  const newestId = new Map<boolean, string>()
  for (const d of sorted) {
    if (d.snapshot_version === liveVersion && !newestId.has(isPdf(d))) newestId.set(isPdf(d), d.document_id)
  }

  return (
    <ul className="space-y-2">
      {sorted.map(doc => (
        <li
          key={doc.document_id}
          className={`flex flex-col gap-1 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${
            doc.retrievable ? 'border-gray-200' : 'border-gray-200 bg-gray-50 text-gray-500'
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className={doc.retrievable ? 'font-medium text-gray-900' : 'font-medium'}>
              {isPdf(doc) ? 'PDF' : 'Word document'}
            </span>
            {badgeFor(doc, liveVersion, newestId) && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                {badgeFor(doc, liveVersion, newestId)}
              </span>
            )}
            <span className="text-xs text-gray-500">{doc.filename}</span>
            <span className="text-xs text-gray-500">{formatDateTime(doc.produced_at)}</span>
            <span className="text-xs text-gray-500">
              Produced by {doc.produced_by_name ?? doc.produced_by}
            </span>
          </div>
          {doc.retrievable ? (
            <button
              type="button"
              onClick={() => handleDownload(doc)}
              disabled={downloadingId === doc.document_id}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              {downloadingId === doc.document_id
                ? 'Downloading…'
                : `Download .${extensionOf(doc)}  ${Math.max(1, Math.round(doc.byte_size / 1024))} KB`}
            </button>
          ) : (
            <span className="text-xs italic text-gray-500">Recorded, file not stored</span>
          )}
        </li>
      ))}
    </ul>
  )
}

export function OverviewDocuments({ id }: { id: string }) {
  const location = useLocation()
  const [documents, setDocuments] = useState<ValuationDocumentRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchValuationDocuments(id)
      .then(res => { if (live) setDocuments(res.documents) })
      .catch((err: unknown) => { if (live) setError(formatApiError(err)) })
    return () => { live = false }
  }, [id])

  // VAL-DOC-ANCHOR-1 — the card grows once documents finish loading, so an
  // effect fired on bare mount would scroll to a position that no longer
  // exists a moment later. Key it on `documents`.
  // VAL-DOC-ANCHOR-2 — a rail click from an already-mounted page is a
  // hash-only navigation with no re-mount, so the effect must also key on
  // `location.hash`, not just read `window.location.hash` once.
  useEffect(() => {
    if (location.hash !== '#documents' || documents === null) return
    document.getElementById('documents')?.scrollIntoView()
  }, [location.hash, documents])

  return (
    <Card id="documents">
      <CardHeader>
        <CardTitle>Issued documents</CardTitle>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        ) : documents === null ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500">No documents issued yet.</p>
        ) : (
          <IssuedDocumentList engagementId={id} documents={documents} />
        )}
      </CardContent>
    </Card>
  )
}
